// Métricas de actividad (requerimientos y órdenes de trabajo) del usuario o de su sucursal.
// Se abre desde el Dashboard ("Ver reportes y más métricas"). Datos: GET /api/dashboard/metricas.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import DashboardLayout from "../layouts/DashboardLayout";
import PageHeader from "../components/layout/PageHeader";
import axios from "../config/axiosConfig";
import { useAuth } from "../context/AuthContext";
import { MESES_CORTO, numero } from "../modules/dashboardFacturacion/utils/formato";

// Series: slots 1-3 de la paleta categórica validada (azul, naranja, aqua), en claro y oscuro.
const COLORES = {
  light: { s1: "#2a78d6", s2: "#eb6834", s3: "#1baf7a", rejilla: "#e1e0d9", eje: "#c3c2b7", tinta: "#898781" },
  dark: { s1: "#3987e5", s2: "#d95926", s3: "#199e70", rejilla: "#2c2c2a", eje: "#383835", tinta: "#898781" },
};

const ETAPAS_REQ = [
  { estados: ["7"], nombre: "Revisión en Sistemas" },
  { estados: ["1"], nombre: "Coordinación" },
  { estados: ["2"], nombre: "Programación" },
  { estados: ["3", "4"], nombre: "Trabajo de campo" },
];
const ESTADOS_OT = { 1: "Emitido", 2: "Detalle", 3: "Asignado", 4: "Procesando", 5: "Entregado", 6: "Concluido" };

const PERIODOS = [3, 6, 12];

const nombreMes = (mes, conAnio) => {
  const [a, m] = mes.split("-");
  return conAnio ? `${MESES_CORTO[Number(m) - 1]} ${a.slice(2)}` : MESES_CORTO[Number(m) - 1];
};

const porcentaje = (parte, total) => (total > 0 ? Math.round((parte * 100) / total) : null);

// ── Piezas ─────────────────────────────────────

function Indicador({ titulo, valor, detalle, icono, color = "primary", cargando }) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            aria-hidden
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              color: `${color}.main`,
              bgcolor: (theme) => alpha(theme.palette[color].main, 0.12),
            }}
          >
            {icono}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {titulo}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.15, fontVariantNumeric: "tabular-nums" }}>
              {cargando ? <Skeleton width={56} /> : valor}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
              {cargando ? <Skeleton width={110} /> : detalle}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function Seccion({ titulo, subtitulo, accion, children }) {
  return (
    <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardHeader
        title={<Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{titulo}</Typography>}
        subheader={subtitulo}
        subheaderTypographyProps={{ variant: "caption" }}
        action={accion}
        sx={{ py: 1.5, borderBottom: 1, borderColor: "divider", "& .MuiCardHeader-action": { alignSelf: "center", m: 0 } }}
      />
      <CardContent sx={{ flex: 1, pt: 2 }}>{children}</CardContent>
    </Card>
  );
}

function Leyenda({ items }) {
  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
      {items.map((i) => (
        <Stack key={i.nombre} direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: i.color }} />
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 600 }}>{i.nombre}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}

// Tooltip: mes + una fila por serie y una fila de total opcional. El texto va en tinta de texto; el color solo en la marca.
function TooltipMes({ active, payload, total }) {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  const fila = payload[0].payload;
  return (
    <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1.5, p: 1.25, boxShadow: theme.shadows[3], minWidth: 170 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 800, mb: 0.5 }}>{fila.etiquetaLarga}</Typography>
      {payload.map((p) => (
        <Stack key={p.dataKey} direction="row" spacing={0.75} alignItems="center" sx={{ py: 0.15 }}>
          <Box sx={{ width: 9, height: 9, borderRadius: "2px", bgcolor: p.color }} />
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", flex: 1 }}>{p.name}</Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{numero(p.value)}</Typography>
        </Stack>
      ))}
      {total && (
        <Stack direction="row" sx={{ mt: 0.5, pt: 0.5, borderTop: 1, borderColor: "divider" }}>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", flex: 1 }}>{total.nombre}</Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{numero(total.valor(fila))}</Typography>
        </Stack>
      )}
    </Box>
  );
}

function TablaMeses({ columnas, filas }) {
  return (
    <TableContainer sx={{ maxHeight: 300 }}>
      <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.5, px: 1, fontSize: 12.5 } }}>
        <TableHead>
          <TableRow>
            {columnas.map((c, i) => (
              <TableCell key={c} align={i ? "right" : "left"} sx={{ fontWeight: 800 }}>{c}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filas.map((f) => (
            <TableRow key={f[0]}>
              {f.map((v, i) => (
                <TableCell key={i} align={i ? "right" : "left"} sx={i ? { fontVariantNumeric: "tabular-nums" } : undefined}>
                  {v}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Lista de barras horizontales (una sola serie): etiqueta, valor y barra proporcional al mayor.
function BarrasLista({ filas, vacio, color }) {
  const max = filas.reduce((m, f) => Math.max(m, f.cantidad), 0) || 1;
  if (!filas.length) {
    return <Typography variant="body2" color="text.secondary">{vacio}</Typography>;
  }
  return (
    <Stack spacing={1.5}>
      {filas.map((f) => (
        <Box key={f.nombre}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="body2" noWrap title={f.nombre}>{f.nombre}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{numero(f.cantidad)}</Typography>
          </Stack>
          <Box sx={{ height: 8, borderRadius: 999, bgcolor: "action.hover", overflow: "hidden" }}>
            <Box sx={{ height: "100%", width: `${Math.max((f.cantidad * 100) / max, 2)}%`, bgcolor: color, borderRadius: 999 }} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

const ejeX = (c) => ({ tick: { fill: c.tinta, fontSize: 12 }, tickLine: false, axisLine: { stroke: c.eje } });
const ejeY = (c) => ({ tick: { fill: c.tinta, fontSize: 12 }, tickLine: false, axisLine: false, allowDecimals: false, width: 36 });

// ── Página ─────────────────────────────────────

export default function MetricasPage() {
  const theme = useTheme();
  const c = COLORES[theme.palette.mode === "dark" ? "dark" : "light"];
  const superficie = theme.palette.background.paper;
  const { user } = useAuth();

  const [ambito, setAmbito] = useState("yo");
  const [meses, setMeses] = useState(6);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [tablaReq, setTablaReq] = useState(false);
  const [tablaOt, setTablaOt] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const { data: d } = await axios.get("/api/dashboard/metricas", { params: { ambito, meses } });
      setData(d?.requerimientos ? d : d?.data);
    } catch (e) {
      console.error("Error al cargar métricas:", e);
      setError(e?.response?.data?.error || "No se pudieron cargar las métricas.");
    } finally {
      setCargando(false);
    }
  }, [ambito, meses]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const sucursal = data?.sucursal || user?.sucursal || "";

  const req = useMemo(() => {
    const porMes = data?.requerimientos?.por_mes || [];
    const conAnio = new Set(porMes.map((m) => m.mes.slice(0, 4))).size > 1;
    const filas = porMes.map((m) => ({
      ...m,
      etiqueta: nombreMes(m.mes, conAnio),
      etiquetaLarga: nombreMes(m.mes, true).replace(/ (\d\d)$/, " 20$1"),
    }));
    const total = porMes.reduce((a, m) => a + m.total, 0);
    const eliminados = porMes.reduce((a, m) => a + m.eliminados, 0);
    const finalizados = porMes.reduce((a, m) => a + m.finalizados, 0);
    const emitidos = total - eliminados;
    const pendientes = ETAPAS_REQ.map((e) => ({
      nombre: e.nombre,
      cantidad: (data?.requerimientos?.pendientes || [])
        .filter((p) => e.estados.includes(String(p.estado)))
        .reduce((a, p) => a + p.cantidad, 0),
    })).filter((p) => p.cantidad > 0);
    return { filas, emitidos, finalizados, pendientes };
  }, [data]);

  const ot = useMemo(() => {
    const porMes = data?.ot?.por_mes || [];
    const conAnio = new Set(porMes.map((m) => m.mes.slice(0, 4))).size > 1;
    // "Registradas" sin las eliminadas, igual que el indicador.
    const filas = porMes.map((m) => ({
      ...m,
      registradas: m.registradas - m.eliminadas,
      etiqueta: nombreMes(m.mes, conAnio),
      etiquetaLarga: nombreMes(m.mes, true).replace(/ (\d\d)$/, " 20$1"),
    }));
    const registradas = filas.reduce((a, m) => a + m.registradas, 0);
    const finalizadas = porMes.reduce((a, m) => a + m.finalizadas, 0);
    const pendientes = (data?.ot?.pendientes || [])
      .slice()
      .sort((a, b) => a.estado - b.estado)
      .map((p) => ({ nombre: ESTADOS_OT[p.estado] || `Estado ${p.estado}`, cantidad: p.cantidad }));
    return { filas, registradas, finalizadas, pendientes };
  }, [data]);

  const pctReq = porcentaje(req.finalizados, req.emitidos);
  const pctOt = porcentaje(ot.finalizadas, ot.registradas);
  const dias = (v) => (v === null || v === undefined ? "—" : String(v).replace(".", ","));
  const textoAmbito = ambito === "yo" ? "emitidos por ti" : `de ${sucursal || "tu sucursal"}`;

  const seriesReq = [
    { clave: "adicion", nombre: "Adición", color: c.s1 },
    { clave: "mantenimiento", nombre: "Mantenimiento", color: c.s2 },
    { clave: "devolucion", nombre: "Devolución", color: c.s3 },
  ];
  const seriesOt = [
    { clave: "registradas", nombre: "Registradas", color: c.s1 },
    { clave: "finalizadas", nombre: "Finalizadas", color: c.s2 },
  ];

  const botonTabla = (activo, cambiar) => (
    <Button size="small" onClick={() => cambiar(!activo)} sx={{ textTransform: "none" }}>
      {activo ? "Ver gráfico" : "Ver tabla"}
    </Button>
  );

  return (
    <DashboardLayout>
      <PageHeader
        title="Métricas"
        subtitle="Requerimientos y órdenes de trabajo por mes"
        backTo="/dashboard"
        actions={
          <Tooltip title="Recargar">
            <span>
              <IconButton size="small" onClick={cargar} disabled={cargando} aria-label="Recargar métricas">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1400, mx: "auto" }}>
        {/* Filtros: una sola fila sobre todos los gráficos */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2.5 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={ambito}
            onChange={(_, v) => v && setAmbito(v)}
            aria-label="De quién son los datos"
          >
            <ToggleButton value="yo" sx={{ textTransform: "none", px: 2 }}>Solo yo</ToggleButton>
            <ToggleButton value="sucursal" sx={{ textTransform: "none", px: 2 }}>
              Mi sucursal{sucursal ? ` (${sucursal})` : ""}
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={meses}
            onChange={(_, v) => v && setMeses(v)}
            aria-label="Período"
          >
            {PERIODOS.map((p) => (
              <ToggleButton key={p} value={p} sx={{ textTransform: "none", px: 2 }}>
                {p} meses
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: "auto" } }}>
            El mes actual cuenta hasta hoy.
          </Typography>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Indicadores del período */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Indicador
              titulo="Requerimientos emitidos"
              valor={numero(req.emitidos)}
              detalle={pctReq === null ? "Sin requerimientos en el período" : `${pctReq}% finalizados`}
              icono={<ReceiptLongIcon />}
              cargando={cargando}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Indicador
              titulo="Cierre de requerimientos"
              valor={`${dias(data?.requerimientos?.dias_cierre)} días`}
              detalle="Promedio de emisión a finalización"
              icono={<TimerOutlinedIcon />}
              color="info"
              cargando={cargando}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Indicador
              titulo="Órdenes de trabajo"
              valor={numero(ot.registradas)}
              detalle={pctOt === null ? "Sin OT en el período" : `${pctOt}% finalizadas`}
              icono={<AssignmentTurnedInIcon />}
              color="success"
              cargando={cargando}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Indicador
              titulo="Cierre de OT"
              valor={`${dias(data?.ot?.dias_cierre)} días`}
              detalle="Promedio de registro a finalización"
              icono={<HourglassBottomIcon />}
              color="warning"
              cargando={cargando}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2.5}>
          {/* Requerimientos por mes, apilados por tipo */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Seccion
              titulo="Requerimientos por mes"
              subtitulo={`Por tipo, ${textoAmbito} (sin eliminados)`}
              accion={botonTabla(tablaReq, setTablaReq)}
            >
              {cargando ? (
                <Skeleton variant="rounded" height={280} />
              ) : tablaReq ? (
                <TablaMeses
                  columnas={["Mes", "Adición", "Mantenimiento", "Devolución", "Finalizados", "Eliminados"]}
                  filas={req.filas.map((m) => [m.etiquetaLarga, m.adicion, m.mantenimiento, m.devolucion, m.finalizados, m.eliminados])}
                />
              ) : (
                <>
                  <Leyenda items={seriesReq} />
                  <Box sx={{ height: 280 }} role="img" aria-label="Requerimientos por mes apilados por tipo: adición, mantenimiento y devolución">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={req.filas} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barCategoryGap="28%">
                        <CartesianGrid vertical={false} stroke={c.rejilla} />
                        <XAxis dataKey="etiqueta" {...ejeX(c)} />
                        <YAxis {...ejeY(c)} />
                        <RTooltip
                          cursor={{ fill: theme.palette.action.hover }}
                          content={<TooltipMes total={{ nombre: "Total (con eliminados)", valor: (f) => f.total }} />}
                        />
                        {seriesReq.map((s, i) => (
                          <Bar
                            key={s.clave}
                            dataKey={s.clave}
                            name={s.nombre}
                            stackId="tipo"
                            fill={s.color}
                            stroke={superficie}
                            strokeWidth={1}
                            maxBarSize={24}
                            radius={i === seriesReq.length - 1 ? [4, 4, 0, 0] : 0}
                            isAnimationActive={false}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              )}
            </Seccion>
          </Grid>

          {/* Pendientes de hoy */}
          <Grid size={{ xs: 12, sm: 6, lg: 5 }}>
            <Seccion titulo="Requerimientos pendientes" subtitulo={`Hoy, por etapa, ${textoAmbito}`}>
              {cargando ? (
                <Skeleton variant="rounded" height={160} />
              ) : (
                <BarrasLista filas={req.pendientes} color={c.s1} vacio="No hay requerimientos pendientes." />
              )}
            </Seccion>
          </Grid>

          {/* OT por mes */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Seccion
              titulo="Órdenes de trabajo por mes"
              subtitulo={`Registradas y finalizadas, ${ambito === "yo" ? "registradas por ti" : `de ${sucursal || "tu sucursal"}`} (sin eliminadas)`}
              accion={botonTabla(tablaOt, setTablaOt)}
            >
              {cargando ? (
                <Skeleton variant="rounded" height={280} />
              ) : tablaOt ? (
                <TablaMeses
                  columnas={["Mes", "Registradas", "Finalizadas", "Eliminadas"]}
                  filas={ot.filas.map((m) => [m.etiquetaLarga, m.registradas, m.finalizadas, m.eliminadas])}
                />
              ) : (
                <>
                  <Leyenda items={seriesOt} />
                  <Box sx={{ height: 280 }} role="img" aria-label="Órdenes de trabajo registradas y finalizadas por mes">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={ot.filas} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barCategoryGap="24%" barGap={2}>
                        <CartesianGrid vertical={false} stroke={c.rejilla} />
                        <XAxis dataKey="etiqueta" {...ejeX(c)} />
                        <YAxis {...ejeY(c)} />
                        <RTooltip cursor={{ fill: theme.palette.action.hover }} content={<TooltipMes />} />
                        {seriesOt.map((s) => (
                          <Bar
                            key={s.clave}
                            dataKey={s.clave}
                            name={s.nombre}
                            fill={s.color}
                            maxBarSize={20}
                            radius={[4, 4, 0, 0]}
                            isAnimationActive={false}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              )}
            </Seccion>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 5 }}>
            <Seccion
              titulo="OT pendientes"
              subtitulo={`Hoy, por estado, ${ambito === "yo" ? "registradas por ti" : `de ${sucursal || "tu sucursal"}`}`}
            >
              {cargando ? (
                <Skeleton variant="rounded" height={160} />
              ) : (
                <BarrasLista filas={ot.pendientes} color={c.s1} vacio="No hay OT pendientes." />
              )}
            </Seccion>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Seccion titulo="Clientes con más requerimientos" subtitulo={`En el período, ${textoAmbito}`}>
              {cargando ? (
                <Skeleton variant="rounded" height={200} />
              ) : (
                <BarrasLista filas={data?.requerimientos?.clientes || []} color={c.s1} vacio="Sin requerimientos en el período." />
              )}
            </Seccion>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Seccion titulo="OT por tipo" subtitulo="En el período, sin eliminadas">
              {cargando ? (
                <Skeleton variant="rounded" height={200} />
              ) : (
                <BarrasLista filas={data?.ot?.tipos || []} color={c.s1} vacio="Sin OT en el período." />
              )}
            </Seccion>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
}
